import { Head, router } from '@inertiajs/react'
import { Check, Loader2, X } from 'lucide-react'
import { useState } from 'react'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import routes from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'

interface PlanPrice {
  id: string
  amount_cents: number
  currency: string
  interval: 'monthly' | 'yearly'
  formatted: string
}

interface PlanEntitlement {
  key: string
  name: string
  value: boolean | number | string
  type: 'boolean' | 'integer' | 'string' | 'json'
}

interface Plan {
  id: string
  name: string
  slug: string
  description: string
  prices: PlanPrice[]
  entitlements: PlanEntitlement[]
}

interface PlansProps {
  plans: Plan[]
  current_plan: string | null
  stripe_publishable_key: string
  current_user: CurrentUser
}

export default function Plans({ plans, current_plan, current_user }: PlansProps) {
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')
  const [loading, setLoading] = useState<string | null>(null)

  const handleSelectPlan = (plan: Plan) => {
    const price = plan.prices.find(p => p.interval === billingInterval)
    if (!price) return

    setLoading(plan.id)
    router.post(routes.billing.checkout.path(), { price_id: price.id }, {
      onFinish: () => setLoading(null)
    })
  }

  const formatValue = (entitlement: PlanEntitlement): string | React.ReactElement => {
    if (entitlement.type === 'boolean') {
      return entitlement.value ? (
        <Check className="size-5 text-primary" />
      ) : (
        <X className="size-5 text-muted-foreground" />
      )
    }
    if (entitlement.type === 'integer') {
      const val = entitlement.value as number
      return val === -1 ? 'Unlimited' : val.toString()
    }
    return String(entitlement.value)
  }

  const yearlySavings = (plan: Plan): number | null => {
    const monthly = plan.prices.find(p => p.interval === 'monthly')?.amount_cents
    const yearly = plan.prices.find(p => p.interval === 'yearly')?.amount_cents
    if (!monthly || !yearly) return null
    return (monthly * 12) - yearly
  }

  return (
    <>
      <Head title="Choose a Plan" />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Billing' }, { label: 'Plans' }]}>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">Choose your plan</h1>
              <p className="text-muted-foreground">Start free, upgrade when you need more</p>
            </div>

            {/* Billing interval toggle */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 p-1 bg-muted rounded-lg">
                <button
                  type="button"
                  onClick={() => setBillingInterval('monthly')}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    billingInterval === 'monthly' ? "bg-background shadow" : "hover:bg-background/50"
                  )}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingInterval('yearly')}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer",
                    billingInterval === 'yearly' ? "bg-background shadow" : "hover:bg-background/50"
                  )}
                >
                  Yearly
                  <span className="ml-1 text-xs text-primary">(Save 2 months)</span>
                </button>
              </div>
            </div>

            {/* Plan cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map(plan => {
                const price = plan.prices.find(p => p.interval === billingInterval)
                const isCurrent = plan.slug === current_plan
                const isFree = !price || price.amount_cents === 0
                const savings = yearlySavings(plan)

                return (
                  <Card
                    key={plan.id}
                    className={cn(
                      "relative flex flex-col h-full",
                      isCurrent && "ring-2 ring-primary"
                    )}
                  >
                    {isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                        Current Plan
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle>{plan.name}</CardTitle>
                      <CardDescription>{plan.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <div className="mb-6">
                        {isFree ? (
                          <div className="text-3xl font-bold">Free</div>
                        ) : (
                          <>
                            <div className="text-3xl font-bold">
                              ${(price!.amount_cents / 100).toFixed(0)}
                              <span className="text-lg font-normal text-muted-foreground">
                                /{billingInterval === 'yearly' ? 'yr' : 'mo'}
                              </span>
                            </div>
                            {billingInterval === 'yearly' && savings && savings > 0 && (
                              <div className="text-sm text-primary">
                                Save ${(savings / 100).toFixed(0)}/year
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <ul className="space-y-3">
                        {plan.entitlements.map(ent => (
                          <li key={ent.key} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{ent.name}</span>
                            <span className="font-medium">{formatValue(ent)}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter className="mt-auto">
                      {isCurrent ? (
                        <Button variant="outline" className="w-full" disabled>
                          Current Plan
                        </Button>
                      ) : isFree ? (
                        <Button variant="outline" className="w-full cursor-pointer" disabled={!!current_plan && current_plan !== 'free'}>
                          {current_plan && current_plan !== 'free' ? 'Contact to downgrade' : 'Get Started'}
                        </Button>
                      ) : (
                        <Button
                          className="w-full cursor-pointer"
                          onClick={() => handleSelectPlan(plan)}
                          disabled={loading === plan.id}
                        >
                          {loading === plan.id ? (
                            <>
                              <Loader2 className="size-4 mr-2 animate-spin" />
                              Redirecting...
                            </>
                          ) : (
                            'Upgrade'
                          )}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                )
              })}
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  )
}
